/**
 * <custom-quick-links>
 *
 * Umschliesst das <quick-links>-Element aus sections/custom-quick-links.liquid und
 * ergaenzt die sticky Sprungnavigation um drei Dinge:
 *
 *   1. Scroll-Spy — der Link des gerade sichtbaren Abschnitts bekommt aria-current,
 *      das Styling dazu steht in assets/custom-quick-links.css.
 *   2. --quick-links-height auf :root, damit scroll-padding-top die Hoehe der
 *      Leiste mitrechnen kann und Sprungziele nicht dahinter landen.
 *   3. [data-scroll-end] auf der Liste, damit die Fade-Kante auf Mobile
 *      verschwindet, sobald ganz nach rechts gescrollt wurde.
 *   4. --cql-top auf :root (Mobile-Fix 2026-10-28): Unterkante des Headers im
 *      angedockten Zustand. --header-end aus assets/main.js wird auf Mobile nur
 *      beim Laden berechnet (inkl. Ankuendigungsleiste) und danach nicht mehr
 *      aktualisiert — die Leiste klebte dadurch mit einer Luecke unter dem Header.
 *      Genutzt nur unter 769 px (assets/custom-quick-links.css).
 *   5. Der aktive Tab wird in der wischbaren Liste sichtbar gescrollt.
 *
 * Auf dem Desktop kommt die Sticky-Position weiter aus --header-end, das
 * store-header (assets/main.js) live pflegt.
 */
if (!customElements.get('custom-quick-links')) {
  class CustomQuickLinks extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.quick-links__list');

      // Sticky ist das Section-Element, nicht dieses hier — Hoehe und Grenzlinie
      // muessen das Padding aus .section--padded-* einschliessen.
      this.bar = this.closest('.cc-quick-links') || this;

      // Nur interne Sprunglinks bekommen einen aktiven Zustand — externe Links
      // und Blocks ohne URL bleiben aussen vor.
      this.links = Array.from(this.querySelectorAll('a[href^="#"]'))
        .map((link) => ({
          link,
          target: document.getElementById(decodeURIComponent(link.hash.slice(1))),
        }))
        .filter((entry) => entry.target);

      this.handleResize = this.handleResize.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleListScroll = this.handleListScroll.bind(this);
      this.updateStickyTop = this.updateStickyTop.bind(this);
      this.handleStickyScroll = this.handleStickyScroll.bind(this);

      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this.bar);

      // Mobile: Andockkante des Headers selbst ermitteln (siehe Punkt 4 oben).
      this.headerSection = document.querySelector('.cc-header');
      if (this.headerSection) {
        this.headerObserver = new ResizeObserver(this.updateStickyTop);
        this.headerObserver.observe(this.headerSection);
      }
      window.addEventListener('scroll', this.handleStickyScroll, { passive: true });
      window.addEventListener('load', this.updateStickyTop);
      this.updateStickyTop();

      if (this.links.length) {
        this.addEventListener('click', this.handleClick);
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        this.updateActiveLink();
      }

      if (this.list) {
        this.list.addEventListener('scroll', this.handleListScroll, { passive: true });
        this.handleListScroll();
      }
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.headerObserver?.disconnect();
      this.removeEventListener('click', this.handleClick);
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('scroll', this.handleStickyScroll);
      window.removeEventListener('load', this.updateStickyTop);
      if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
      document.documentElement.style.removeProperty('--cql-top');
      this.list?.removeEventListener('scroll', this.handleListScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
      document.documentElement.style.removeProperty('--quick-links-height');
    }

    /**
     * Untere Kante der Leiste im Viewport. Solange sie sticky klebt, ist das die
     * Linie, ab der ein Abschnitt als "aktuell" gilt. Vor dem Kleben liegt sie
     * weiter unten — dann ist ohnehin noch kein Sprungziel passiert.
     * @returns {number}
     */
    get boundary() {
      return this.bar.getBoundingClientRect().bottom;
    }

    /**
     * Drosselt die Auswertung auf einen Frame pro Scroll-Burst.
     */
    handleScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateActiveLink();
      });
    }

    /**
     * Setzt aria-current auf den letzten Link, dessen Ziel oberhalb der
     * Leistenunterkante liegt. Liegt noch keiner darueber, bleibt der erste aktiv.
     */
    updateActiveLink() {
      // + Abstand aus scroll-padding-top (2 × --space-unit = 8 px, custom-quick-links.css)
      // plus Toleranz – sonst gilt nach einem Tab-Klick noch der vorige Abschnitt als aktiv.
      const boundary = this.boundary + 12;
      let activeIndex = 0;

      // Erst alle Positionen lesen, dann schreiben — kein Layout-Thrashing.
      this.links.forEach(({ target }, index) => {
        if (target.getBoundingClientRect().top <= boundary) activeIndex = index;
      });

      this.setActiveLink(activeIndex);
    }

    /**
     * @param {number} activeIndex - Index in this.links.
     */
    setActiveLink(activeIndex) {
      if (activeIndex === this.activeIndex) return;
      this.activeIndex = activeIndex;

      this.links.forEach(({ link }, index) => {
        if (index === activeIndex) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });

      this.revealActiveLink();
    }

    /**
     * Scrollt die wischbare Tab-Liste (Mobile) so, dass der aktive Tab sichtbar ist.
     * Nur die Liste wird horizontal bewegt – nie die Seite (kein scrollIntoView).
     */
    revealActiveLink() {
      const list = this.list;
      const active = this.links[this.activeIndex]?.link;
      if (!list || !active || list.scrollWidth <= list.clientWidth + 1) return;

      const pad = parseFloat(getComputedStyle(list).paddingLeft) || 0;
      const listRect = list.getBoundingClientRect();
      const linkRect = active.getBoundingClientRect();
      const fade = 24; // Breite der Fade-Kante rechts (custom-quick-links.css)

      let delta = 0;
      if (linkRect.left < listRect.left + pad) {
        delta = linkRect.left - listRect.left - pad;
      } else if (linkRect.right > listRect.right - fade) {
        delta = linkRect.right - listRect.right + fade + pad;
      }
      if (Math.abs(delta) < 1) return;

      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      list.scrollTo({ left: list.scrollLeft + delta, behavior: reduce ? 'auto' : 'smooth' });
    }

    /**
     * Drosselt updateStickyTop auf einen Frame pro Scroll-Burst. Beim Laden wird
     * .cc-header--sticky erst per setTimeout gesetzt (assets/main.js) – deshalb
     * auch beim Scrollen nachrechnen; geschrieben wird nur bei Aenderung.
     */
    handleStickyScroll() {
      if (this.stickyFrame) return;
      this.stickyFrame = requestAnimationFrame(() => {
        this.stickyFrame = null;
        this.updateStickyTop();
      });
    }

    /**
     * Setzt --cql-top = Unterkante des Headers, wenn er oben angedockt ist
     * (sticky-top + Hoehe). Ist der Header nicht sticky, ist der Wert 0.
     */
    updateStickyTop() {
      const header = this.headerSection;
      if (!header) return;

      const style = getComputedStyle(header);
      let top = 0;
      if (style.position === 'sticky' || style.position === 'fixed') {
        top = Math.max(0, Math.round(header.getBoundingClientRect().height + (parseFloat(style.top) || 0)));
      }
      if (top === this.lastStickyTop) return;

      this.lastStickyTop = top;
      document.documentElement.style.setProperty('--cql-top', `${top}px`);
    }

    /**
     * Klick auf einen Tab markiert ihn sofort, ohne aufs Scrollen zu warten.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const link = evt.target.closest('a[href^="#"]');
      if (!link) return;

      const index = this.links.findIndex((entry) => entry.link === link);
      if (index !== -1) this.setActiveLink(index);
    }

    /**
     * Haelt --quick-links-height aktuell — Grundlage fuer scroll-padding-top in
     * assets/custom-quick-links.css.
     */
    handleResize() {
      const height = Math.round(this.bar.getBoundingClientRect().height);
      if (height === this.lastHeight) return;

      this.lastHeight = height;
      document.documentElement.style.setProperty('--quick-links-height', `${height}px`);
    }

    /**
     * Blendet die Fade-Kante aus, sobald die Liste ganz nach rechts gescrollt ist.
     */
    handleListScroll() {
      const remaining = this.list.scrollWidth - this.list.clientWidth - this.list.scrollLeft;
      this.list.toggleAttribute('data-scroll-end', remaining <= 1);
    }
  }

  customElements.define('custom-quick-links', CustomQuickLinks);
}
